// Shared storage/DB-write mechanics for an incident report photo, used by
// every photo field (guided vehicle-damage walkthrough, the simpler
// live-camera fields for scene/other-vehicle photos) so there's one copy
// of the upload path convention and insurance_incident_photos insert.
import { supabase } from "@/lib/supabase";
import { addIncidentPhoto, deleteIncidentPhoto } from "@/lib/incidentReports";

export interface IncidentPhotoRef {
  id: string;
  storage_path: string;
  url: string;
}

export const incidentPhotoPublicUrl = (path: string) =>
  supabase.storage.from("incident-reports").getPublicUrl(path).data.publicUrl;

export async function uploadIncidentPhotoBlob(params: {
  incidentId: string;
  category: string;
  blob: Blob;
  /** Short tag folded into the filename for readability (e.g. a view name) -- not stored as its own field. */
  filenameHint: string;
  location: { lat: number; lng: number } | null;
  uploadedBy: string;
}): Promise<IncidentPhotoRef> {
  const path = `incidents/${params.incidentId}/${params.category}_${params.filenameHint}_${Date.now()}.jpg`;
  const { error: uploadError } = await supabase.storage.from("incident-reports").upload(path, params.blob, {
    contentType: "image/jpeg",
    upsert: true,
  });
  if (uploadError) throw uploadError;
  const row = await addIncidentPhoto({
    incident_id: params.incidentId,
    category: params.category,
    storage_path: path,
    latitude: params.location?.lat ?? null,
    longitude: params.location?.lng ?? null,
    taken_at: new Date().toISOString(),
    uploaded_by: params.uploadedBy,
  });
  return { id: row.id, storage_path: path, url: incidentPhotoPublicUrl(path) };
}

export async function removeIncidentPhoto(photo: IncidentPhotoRef): Promise<void> {
  await deleteIncidentPhoto(photo.id);
  await supabase.storage.from("incident-reports").remove([photo.storage_path]);
}
